import React from 'react';
import PropTypes from 'prop-types';
import { ComplianceCard } from '../shared';
import './ComplianceStatusGrid.css';

/**
 * Convert status string to user-friendly display text
 */
const formatStatus = (status) => {
  if (!status) return 'Not Configured';

  const statusMap = {
    'twilio-approved': 'Approved',
    'approved': 'Approved',
    'verified': 'Approved',
    'pending-review': 'Pending Review',
    'in-review': 'In Review',
    'pending': 'Pending',
    'pending_trusthub': 'Business Profile in Review',
    'pending-trusthub': 'Business Profile in Review',
    'pending_a2p_trust_product': 'Trust Product in Review',
    'pending-a2p-trust-product': 'Trust Product in Review',
    'twilio-rejected': 'Rejected',
    'rejected': 'Rejected',
    'failed': 'Failed',
    'draft': 'Draft',
    'active': 'Active',
    'inactive': 'Inactive',
    'not_created': 'Not Created',
    'not-created': 'Not Created',
    'not_configured': 'Not Configured',
    'not-configured': 'Not Configured',
    'not_registered': 'Not Registered',
    'not-registered': 'Not Registered',
    'cancelled': 'Cancelled',
    'expired': 'Expired',
  };

  return statusMap[status] || status;
};

/**
 * ComplianceStatusGrid Component
 * Displays grid of compliance status cards for all compliance areas
 */
const ComplianceStatusGrid = ({ dashboardData, onCardClick }) => {
  if (!dashboardData) return null;

  // Check if profile is approved
  const isProfileApproved = dashboardData.business_profile?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(dashboardData.business_profile.status);

  const businessProfileDetails = dashboardData.business_profile?.exists
    ? [
        {
          label: 'Profile Name',
          value: dashboardData.business_profile.friendly_name || 'N/A',
        },
        {
          label: 'Last Updated',
          value: dashboardData.business_profile.last_updated || 'N/A',
        },
      ]
    : [];

  // Voice compliance details - STIR/SHAKEN always on top
  const voiceComplianceDetails = [
    {
      label: 'SHAKEN/STIR Attestation',
      value: formatStatus(dashboardData.voice_compliance?.shaken_stir?.status),
    },
    {
      label: 'CNAM',
      value: formatStatus(dashboardData.voice_compliance?.cnam?.status),
    },
    {
      label: 'Voice Integrity',
      value: formatStatus(dashboardData.voice_compliance?.voice_integrity?.status),
    },
  ];

  const smsComplianceDetails = [
    {
      label: 'Brand Status',
      value: formatStatus(dashboardData.sms_compliance?.brand?.status) || 'Not Registered',
    },
    {
      label: 'Campaign Status',
      value: formatStatus(dashboardData.sms_compliance?.campaign?.status) || 'Not Registered',
    },
    {
      label: 'Phones Assigned',
      value: dashboardData.sms_compliance?.phones_assigned || 0,
    },
  ];

  return (
    <div className="compliance-status-grid">
      <ComplianceCard
        title="Business Profile"
        description="Business Profile Verification"
        status={dashboardData.business_profile?.status || 'not_created'}
        details={businessProfileDetails}
        actions={
          dashboardData.business_profile?.exists
            ? [
                {
                  label: 'View Details',
                  onClick: () => onCardClick('profile'),
                  variant: 'secondary',
                },
              ]
            : [
                {
                  label: 'Create Profile',
                  onClick: () => onCardClick('profile'),
                  variant: 'primary',
                },
              ]
        }
      />

      <ComplianceCard
        title="Voice Compliance"
        description="CNAM, SHAKEN/STIR, Voice Integrity"
        status={
          dashboardData.voice_compliance?.cnam?.status === 'twilio-approved' &&
          dashboardData.voice_compliance?.shaken_stir?.status === 'twilio-approved'
            ? 'twilio-approved'
            : 'not-configured'
        }
        details={voiceComplianceDetails}
        actions={[
          {
            label: 'Manage Voice',
            onClick: () => onCardClick('voice'),
            variant: 'secondary',
            disabled: !isProfileApproved,
          },
        ]}
      />

      <ComplianceCard
        title="SMS Compliance"
        description="Brand and Campaign registration"
        status={dashboardData.sms_compliance?.brand?.status || 'not_registered'}
        details={smsComplianceDetails}
        actions={[
          {
            label: 'Manage SMS',
            onClick: () => onCardClick('sms'),
            variant: 'secondary',
            disabled: !isProfileApproved,
          },
        ]}
      />
    </div>
  );
};

ComplianceStatusGrid.propTypes = {
  dashboardData: PropTypes.object.isRequired,
  onCardClick: PropTypes.func.isRequired,
};

export default ComplianceStatusGrid;
